import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  FiMapPin,
  FiCalendar,
  FiMaximize,
  FiArrowLeft,
  FiShare2,
  FiPhone,
  FiMail,
  FiCheck,
} from "react-icons/fi";
import { IoBedOutline } from "react-icons/io5";
import { LuBath, LuCar } from "react-icons/lu";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import properties from "../data/properties";
import Card from "../Components/Card";
import { useApp } from "../context/AppContext";

export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, toggleFavorite, isFavorite, addNotification } = useApp();
  const [activeImg, setActiveImg] = useState(0);
  const [contactMsg, setContactMsg] = useState("");

  const property = properties.find((p) => p.id === Number(id));

  if (!property) {
    return (
      <div className="page-wrapper" style={{ marginTop: "62px" }}>
        <div className="container py-5">
          <div className="empty-state">
            <div className="empty-icon">🏠</div>
            <h3>Property Not Found</h3>
            <p>
              The property you're looking for doesn't exist or has been removed.
            </p>
            <Link
              to="/properties"
              className="btn btn-primary rounded-pill px-4"
            >
              Browse Properties
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const similar = properties
    .filter((p) => p.id !== property.id && p.location === property.location)
    .slice(0, 3);

  const moreSimilar =
    similar.length < 3
      ? [
          ...similar,
          ...properties
            .filter(
              (p) =>
                p.id !== property.id &&
                p.location !== property.location &&
                p.type === property.type
            )
            .slice(0, 3 - similar.length),
        ]
      : similar;

  const fav = isFavorite(property.id);

  const handleContact = (e) => {
    e.preventDefault();
    addNotification("Message sent to the agent successfully!", "success");
    setContactMsg("");
  };

  return (
    <div className="page-wrapper" style={{ marginTop: "62px" }}>
      <div className="container py-4">
        {/* Back Button & Actions */}
        <div className="d-flex justify-content-between align-items-center mb-4 fade-in">
          <button
            className="btn btn-outline-secondary rounded-pill d-flex align-items-center gap-2"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft /> Back
          </button>
          <div className="d-flex gap-2">
            {user && (
              <button
                className={`btn rounded-pill d-flex align-items-center gap-2 ${
                  fav ? "btn-danger" : "btn-outline-danger"
                }`}
                onClick={() => toggleFavorite(property.id)}
              >
                {fav ? <FaHeart /> : <FaRegHeart />}
                {fav ? "Saved" : "Save"}
              </button>
            )}
            <button
              className="btn btn-outline-secondary rounded-pill d-flex align-items-center gap-2"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                addNotification("Link copied to clipboard!", "info");
              }}
            >
              <FiShare2 /> Share
            </button>
          </div>
        </div>

        <div className="row g-4">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Image Gallery */}
            <div className="details-hero mb-4 fade-in-up">
              <img
                src={property.images[activeImg]}
                alt={property.title}
                className="w-100"
                style={{
                  height: 450,
                  objectFit: "cover",
                  borderRadius: "var(--radius-lg)",
                }}
              />
              <div className="d-flex gap-2 mt-2">
                {property.images.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`View ${i + 1}`}
                    className={`rounded-3 ${
                      activeImg === i
                        ? "border border-primary border-3"
                        : ""
                    }`}
                    style={{
                      width: 100,
                      height: 70,
                      objectFit: "cover",
                      cursor: "pointer",
                      opacity: activeImg === i ? 1 : 0.6,
                      transition: "all 0.3s",
                    }}
                    onClick={() => setActiveImg(i)}
                  />
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="details-info-card mb-4 fade-in-up-delay-1">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                  <div className="details-price">
                    ${property.price.toLocaleString()}
                  </div>
                  <h2 className="details-title">{property.title}</h2>
                  <div className="details-location">
                    <FiMapPin /> {property.location}, Kigali, Rwanda
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <span
                    className={`badge rounded-pill fs-6 px-3 py-2 ${
                      property.status === "For Sale"
                        ? "bg-primary"
                        : "bg-success"
                    }`}
                  >
                    {property.status}
                  </span>
                  <span className="badge rounded-pill fs-6 px-3 py-2 bg-secondary">
                    {property.type}
                  </span>
                </div>
              </div>

              {/* Features Grid */}
              <div className="d-flex flex-wrap gap-3 mb-4">
                {property.bedrooms > 0 && (
                  <div className="feature-badge">
                    <IoBedOutline size={20} />
                    <span>{property.bedrooms} Bedrooms</span>
                  </div>
                )}
                <div className="feature-badge">
                  <LuBath size={18} />
                  <span>{property.bathrooms} Bathrooms</span>
                </div>
                <div className="feature-badge">
                  <LuCar size={18} />
                  <span>{property.parking} Parking</span>
                </div>
                <div className="feature-badge">
                  <FiMaximize size={18} />
                  <span>{property.area} m²</span>
                </div>
                <div className="feature-badge">
                  <FiCalendar size={18} />
                  <span>Built {property.yearBuilt}</span>
                </div>
              </div>

              {/* Description */}
              <h5 className="fw-bold mb-3">Description</h5>
              <p className="text-muted" style={{ lineHeight: 1.8 }}>
                {property.description}
              </p>

              {/* Amenities */}
              <h5 className="fw-bold mb-3 mt-4">Amenities</h5>
              <div className="d-flex flex-wrap gap-2">
                {property.amenities.map((amenity, i) => (
                  <span key={i} className="amenity-tag">
                    <FiCheck size={14} /> {amenity}
                  </span>
                ))}
              </div>
            </div>

            {/* Location Info */}
            <div className="details-info-card fade-in-up-delay-2">
              <h5 className="fw-bold mb-3">Location</h5>
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  height: 200,
                  background: "var(--gray-100)",
                  border: "1px solid var(--gray-200)",
                }}
              >
                <div className="text-center text-muted">
                  <FiMapPin size={32} className="mb-2" />
                  <p className="mb-0">{property.location}, Kigali, Rwanda</p>
                  <small>Interactive map coming soon</small>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* Agent Card */}
            <div className="agent-card mb-4 fade-in-up-delay-1">
              <div className="agent-avatar">
                {property.agent.name.charAt(0)}
              </div>
              <h5 className="fw-bold mb-1">{property.agent.name}</h5>
              <p className="text-muted small mb-3">Licensed Real Estate Agent</p>
              <div className="d-grid gap-2 mb-3">
                <a
                  href={`tel:${property.agent.phone}`}
                  className="btn btn-primary rounded-pill d-flex align-items-center justify-content-center gap-2"
                >
                  <FiPhone /> {property.agent.phone}
                </a>
                <a
                  href={`mailto:${property.agent.email}`}
                  className="btn btn-outline-primary rounded-pill d-flex align-items-center justify-content-center gap-2"
                >
                  <FiMail /> Send Email
                </a>
              </div>

              <form onSubmit={handleContact}>
                <textarea
                  className="form-control mb-2"
                  rows={3}
                  placeholder="Hi, I'm interested in this property..."
                  value={contactMsg}
                  onChange={(e) => setContactMsg(e.target.value)}
                  style={{
                    borderRadius: 10,
                    border: "2px solid var(--gray-200)",
                  }}
                ></textarea>
                <button
                  className="btn btn-primary w-100 rounded-pill"
                  type="submit"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Quick Facts */}
            <div className="details-info-card mb-4 fade-in-up-delay-2">
              <h6 className="fw-bold mb-3">Quick Facts</h6>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted">Property Type</td>
                    <td className="fw-semibold text-end">{property.type}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Year Built</td>
                    <td className="fw-semibold text-end">{property.yearBuilt}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Total Area</td>
                    <td className="fw-semibold text-end">{property.area} m²</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Price per m²</td>
                    <td className="fw-semibold text-end">
                      ${Math.round(property.price / property.area).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted border-0">Listed</td>
                    <td className="fw-semibold text-end border-0">
                      {new Date(property.dateAdded).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Similar Properties */}
            {moreSimilar.length > 0 && (
              <div className="details-info-card fade-in-up-delay-3">
                <h6 className="fw-bold mb-3">Similar Properties</h6>
                <div className="d-flex flex-column gap-3">
                  {moreSimilar.map((p) => (
                    <Link
                      key={p.id}
                      to={`/property/${p.id}`}
                      className="similar-card text-decoration-none"
                    >
                      <img src={p.image} alt={p.title} />
                      <div className="p-2">
                        <div
                          className="fw-bold text-primary"
                          style={{ fontSize: "0.9rem" }}
                        >
                          ${p.price.toLocaleString()}
                        </div>
                        <div className="text-dark small">{p.title}</div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <FiMapPin size={10} /> {p.location}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
